import { useCallback, useEffect, useState } from "react";
import { FlaskConical, CheckCircle2, XCircle, Trash2 } from "lucide-react";
import api from "@/api";
import { toast } from "sonner";
import { LabMarkerChart } from "./LabMarkerChart";
import { LabValueForm } from "./LabValueForm";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "—");

export const LabHistoryTab = ({ patientId, canEdit = true }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/patients/${patientId}/lab-values/history`);
      setHistory(data);
    } catch {
      toast.error("Impossible de charger l'historique biologique");
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const validate = async (biologyResultId, accepted) => {
    setBusy(biologyResultId);
    try {
      await api.post(`/patients/${patientId}/lab-values/validate`, { biologyResultId, accepted });
      toast.success(accepted ? "Valeurs OCR validées — intégrées à l'historique" : "Valeurs OCR écartées");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Validation impossible");
    } finally {
      setBusy(null);
    }
  };

  const remove = async (valueId) => {
    if (!window.confirm("Supprimer cette valeur saisie manuellement ?")) return;
    try {
      await api.delete(`/patients/${patientId}/lab-values/${valueId}`);
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  if (loading) return <div className="dash-card text-sm text-slate-500" data-testid="lab-history-loading">Chargement de la biologie…</div>;
  if (!history) return null;

  const withData = history.markers.filter((m) => m.points.some((p) => !p.rejected));
  const manualRows = history.markers.flatMap((m) => m.points.filter((p) => p.source === "MANUAL").map((p) => ({ ...p, label: m.label })));

  return (
    <div className="space-y-4" data-testid="lab-history-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-700">
          <FlaskConical className="w-5 h-5 text-teal-600" />
          <span className="font-semibold">Biologie</span>
          <span className="text-xs text-slate-500" data-testid="lab-history-totals">{history.totals.validated} valeur(s) validée(s) · {history.totals.pending} à valider</span>
        </div>
        {canEdit && <LabValueForm patientId={patientId} markers={history.markers} onSaved={load} />}
      </div>

      {history.pendingResults.length > 0 && canEdit && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-2" data-testid="lab-pending-block">
          <p className="text-sm font-medium text-amber-900">Lectures OCR à valider avant intégration à l'historique et à la data</p>
          {history.pendingResults.map((p) => (
            <div key={p.biologyResultId} className="flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg px-3 py-2 border border-amber-100" data-testid={`lab-pending-${p.biologyResultId}`}>
              <span className="text-sm text-slate-700">Bilan du {fmtDate(p.sampleDate)} — {p.count} valeur(s) : {p.markers.join(", ")}</span>
              <div className="flex gap-2">
                <button disabled={busy === p.biologyResultId} onClick={() => validate(p.biologyResultId, true)} data-testid={`lab-validate-ok-${p.biologyResultId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100"><CheckCircle2 className="w-3.5 h-3.5" /> OCR fiable</button>
                <button disabled={busy === p.biologyResultId} onClick={() => validate(p.biologyResultId, false)} data-testid={`lab-validate-ko-${p.biologyResultId}`} className="inline-flex items-center gap-1 text-xs font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2.5 py-1.5 hover:bg-rose-100"><XCircle className="w-3.5 h-3.5" /> Non fiable</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {withData.length === 0 ? (
        <div className="dash-card text-sm text-slate-500" data-testid="lab-history-empty">Aucune valeur biologique structurée pour ce patient. Les bilans uploadés sont lus automatiquement (OCR) ; vous pouvez aussi saisir une valeur.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {withData.map((m) => <LabMarkerChart key={m.key} marker={m} />)}
        </div>
      )}

      {manualRows.length > 0 && canEdit && (
        <div className="dash-card" data-testid="lab-manual-list">
          <h4 className="text-sm font-semibold text-slate-700 mb-2">Valeurs saisies manuellement</h4>
          <ul className="divide-y divide-slate-100 text-sm">
            {manualRows.map((r) => (
              <li key={r.id} className="flex items-center justify-between py-1.5">
                <span>{fmtDate(r.sampleDate)} · {r.label} : <strong>{r.value} {r.unit}</strong>{r.ctcaeGrade > 0 ? ` (G${r.ctcaeGrade})` : ""}</span>
                <button onClick={() => remove(r.id)} data-testid={`lab-delete-${r.id}`} className="p-1 text-slate-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LabHistoryTab;
